// Patch ResizeObserver to debounce callbacks — prevents the "loop completed with undelivered
// notifications" error that dnd-kit triggers during drag overlay rendering. The error only
// surfaces when DevTools is closed because DevTools suppresses it natively.
if (typeof window !== 'undefined' && window.ResizeObserver) {
  const OriginalResizeObserver = window.ResizeObserver
  window.ResizeObserver = class ResizeObserver extends OriginalResizeObserver {
    constructor(callback) {
      super((entries, observer) => {
        window.requestAnimationFrame(() => {
          if (!Array.isArray(entries) || !entries.length) return
          callback(entries, observer)
        })
      })
    }
  }
}

import React from 'react'
import ReactDOM from 'react-dom'
import App from './App'
import { Provider } from 'react-redux'
import firebase from 'firebase/app'
import 'firebase/auth'
import 'firebase/storage'
import 'firebase/firestore'
import { Collection } from 'constants/index'
import { FIREBASE_CONFIG } from 'config/firebase-config'
import authSlice from 'store/auth'
import store from 'store'
import { isConferenceLive, getAssignedConferenceIds, isConferenceUser } from 'utils/conference'
import './index.css'

firebase.initializeApp(FIREBASE_CONFIG)
export const FirestoreDatabase = firebase.firestore()
// Ad blockers / privacy extensions often flag the default WebChannel "Listen/channel"
// requests as tracking beacons and silently drop them (net::ERR_BLOCKED_BY_CLIENT),
// which kills onSnapshot listeners with no visible error. Auto-detect and fall back
// to plain long-polling when that happens.
FirestoreDatabase.settings({ experimentalAutoDetectLongPolling: true })
export const storage = firebase.storage()
export const storageRef = storage.ref('images')
store.dispatch(authSlice.actions.setProgress(true))

let unsubscribeUserListener = null

// Does the user's own document still grant access to what they're currently viewing?
// Three server-side shapes revoke it: the whole doc deleted (organizer delete, or an
// admin's last conference), the account flagged inactive, and — for admins/owners —
// the current conference either flagged inactive or unassigned. Unassignment removes
// the id from conferenceIds and drops the conferenceStatus/conferenceRoles keys, so a
// missing key has to count as revoked, not just an explicit 'in-active'.
const isSessionRevoked = (doc, currentConferenceId) => {
  if (!doc.exists) return true

  const data = doc.data()
  if (data?.status === 'in-active') return true

  // No conference selected yet (super admin, or mid-login before the picker) — only
  // the account-level checks above apply.
  if (!currentConferenceId) return false
  if (data?.conferenceStatus?.[currentConferenceId] === 'in-active') return true

  const assignedIds = getAssignedConferenceIds(data)
  return assignedIds.length > 0 && !assignedIds.includes(currentConferenceId)
}

// Drop conferences the user was just unassigned from out of the switcher. Without
// this they could still switch into one they no longer belong to — switching only
// changes auth.user.conferenceId locally, so it produces no user-doc change for the
// guard below to react to.
const syncConferenceAssignments = (userData) => {
  const { conferenceAssignments } = store.getState().auth
  if (!conferenceAssignments.length) return

  const assignedIds = getAssignedConferenceIds(userData)
  if (!assignedIds.length) return

  const remaining = conferenceAssignments.filter((a) => assignedIds.includes(a.conferenceId))
  if (remaining.length !== conferenceAssignments.length) {
    store.dispatch(authSlice.actions.setConferenceAssignments(remaining))
  }
}

// Real-time session guard on the user's own document. Must be attached on every path
// that establishes a session — including the signIn thunk's, which owns user-setup
// but never subscribes itself; skipping it there left form logins with nothing
// watching the user doc until the next page load. currentConferenceId is read fresh
// from the store on each snapshot (not captured at subscribe time) since this
// listener stays subscribed across in-app conference switches.
const attachUserSessionGuard = (userId) => {
  if (unsubscribeUserListener) {
    unsubscribeUserListener()
    unsubscribeUserListener = null
  }

  unsubscribeUserListener = firebase
    .firestore()
    .collection(Collection.USERS)
    .doc(userId)
    .onSnapshot(
      (doc) => {
        if (isSessionRevoked(doc, store.getState().auth.user?.conferenceId)) {
          firebase.auth().signOut()
          return
        }
        syncConferenceAssignments(doc.data())
      },
      (error) => {
        // Without this, permission-denied (or any other) errors fail silently and the
        // listener just stops working with no visible symptom. Auth-level failures are
        // treated as fail-closed: losing read access to your own user document means
        // the account or its Auth record is gone, which is the very case this guard
        // exists to catch (deleting the Auth user can kill this stream before the
        // document-delete event is delivered).
        console.error('User document listener failed:', error)
        if (error?.code === 'permission-denied' || error?.code === 'unauthenticated') {
          firebase.auth().signOut()
        }
      }
    )
}

// Live guard: sign out if the conference the user is currently viewing gets
// archived/deactivated mid-session. Driven by store.subscribe (rather than
// onAuthStateChanged) because in-app conference switching (AdminTopBar's
// switcher) changes state.auth.user.conferenceId without any auth-state event.
let unsubscribeConferenceListener = null
let watchedConferenceId = undefined

const watchConferenceLiveness = (conferenceId) => {
  if (conferenceId === watchedConferenceId) return
  watchedConferenceId = conferenceId

  if (unsubscribeConferenceListener) {
    unsubscribeConferenceListener()
    unsubscribeConferenceListener = null
  }
  if (!conferenceId) return

  unsubscribeConferenceListener = firebase
    .firestore()
    .collection(Collection.CONFERENCES)
    .doc(conferenceId)
    .onSnapshot(
      (doc) => {
        if (!doc.exists || !isConferenceLive(doc.data())) {
          firebase.auth().signOut()
        }
      },
      (error) => {
        console.error('Conference document listener failed:', error)
      }
    )
}

store.subscribe(() => {
  watchConferenceLiveness(store.getState().auth.user?.conferenceId)
})

firebase.auth().onAuthStateChanged(async (fireUser) => {
  if (unsubscribeUserListener) {
    unsubscribeUserListener()
    unsubscribeUserListener = null
  }

  // If signIn thunk is actively running, it owns the user-setup — skip here to avoid a
  // race. The session guard still has to be attached: signIn holds loginInProgress from
  // before signInWithEmailAndPassword until after its Firestore fetches (until the
  // conference picker is confirmed, on the multi-conference path), so this branch is the
  // one every form login takes, and nothing else subscribes to the user document.
  if (store.getState().auth.loginInProgress) {
    if (fireUser) attachUserSessionGuard(fireUser.uid)
    store.dispatch(authSlice.actions.setProgress(false))
    return
  }

  if (fireUser) {
    const user = {
      id: fireUser.uid,
      email: fireUser.email
    }
    const snapshot = await firebase.firestore().collection(Collection.USERS).doc(user.id).get()
    if (!snapshot.exists) {
      // Firebase Auth exists but no Firestore profile — sign out to avoid a
      // partial user object (no conferenceId) being pushed into Redux and
      // causing a redirect to /conference/undefined.
      firebase.auth().signOut()
      store.dispatch(authSlice.actions.setProgress(false))
      return
    }
    const userData = snapshot.data()

    if (isConferenceUser(userData)) {
      // Restore multi-conference assignments on page refresh
      const assignedIds = getAssignedConferenceIds(userData)

      if (assignedIds.length) {
        const confSnapshots = await Promise.all(
          assignedIds.map((id) =>
            firebase.firestore().collection(Collection.CONFERENCES).doc(id).get()
          )
        )
        const activeConfs = confSnapshots
          .map((snap) => ({ id: snap.id, ...snap.data() }))
          .filter(isConferenceLive)

        const now = new Date()
        const assignments = activeConfs.map((c) => {
          const rawUntil = c.viewableUntil
          const until = rawUntil?.toDate ? rawUntil.toDate() : rawUntil ? new Date(rawUntil) : null
          return {
            conferenceId: c.id,
            confName: c.confName,
            role: userData.conferenceRoles?.[c.id] ?? userData.role,
            isExpired: until ? until < now : false
          }
        })

        const savedConfId = localStorage.getItem(`selectedConferenceId_${user.id}`)
        const savedAssignment = assignments.find(
          (a) => a.conferenceId === savedConfId && !a.isExpired
        )
        const activeAssignment =
          savedAssignment || assignments.find((a) => !a.isExpired) || assignments[0]

        if (!activeAssignment) {
          // No live conference remains for this admin — don't restore them into a
          // stale/archived one.
          firebase.auth().signOut()
          store.dispatch(authSlice.actions.setProgress(false))
          return
        }

        store.dispatch(authSlice.actions.setConferenceAssignments(assignments))
        store.dispatch(
          authSlice.actions.setUser({
            ...user,
            ...userData,
            conferenceId: activeAssignment.conferenceId
          })
        )
      } else {
        store.dispatch(authSlice.actions.setUser({ ...user, ...userData }))
      }
    } else {
      if (userData?.conferenceId) {
        const confSnapshot = await firebase
          .firestore()
          .collection(Collection.CONFERENCES)
          .doc(userData.conferenceId)
          .get()
        if (!confSnapshot.exists || !isConferenceLive(confSnapshot.data())) {
          firebase.auth().signOut()
          store.dispatch(authSlice.actions.setProgress(false))
          return
        }
      }
      store.dispatch(authSlice.actions.setUser({ ...user, ...userData }))
    }

    store.dispatch(authSlice.actions.setAuthenticated(true))
    store.dispatch(authSlice.actions.setProgress(false))

    attachUserSessionGuard(user.id)
  } else {
    store.dispatch(authSlice.actions.setUser(null))
    store.dispatch(authSlice.actions.setAuthenticated(false))
    store.dispatch(authSlice.actions.setProgress(false))
  }
})

ReactDOM.render(
  <Provider store={store}>
    <App />
  </Provider>,
  document.getElementById('root')
)
